import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { AddCardModal } from '../components/AddCardModal';
import { AddApplePayButton } from '../components/AddApplePayButton';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { colors } from '../constants/colors';
import { getPaymentMethods, deletePaymentMethod, setDefaultPaymentMethod } from '../api/client';
import type { PaymentMethod } from '../types';
import { Plus, CreditCard, Check, Trash2, ArrowLeft } from 'lucide-react';

// Get card brand display name and color
function getCardBrandInfo(brand: string): { name: string; color: string } {
  const brands: Record<string, { name: string; color: string }> = {
    visa: { name: 'Visa', color: '#1A1F71' },
    mastercard: { name: 'Mastercard', color: '#EB001B' },
    amex: { name: 'American Express', color: '#006FCF' },
    discover: { name: 'Discover', color: '#FF6000' },
    diners: { name: 'Diners Club', color: '#0079BE' },
    jcb: { name: 'JCB', color: '#0B4EA2' },
    unionpay: { name: 'UnionPay', color: '#D10429' },
  };
  return brands[brand.toLowerCase()] || { name: brand, color: colors.gray[600] };
}

export function PaymentMethodsPage() {
  useDocumentTitle('Payment Methods');
  const navigate = useNavigate();
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    loadPaymentMethods();
  }, []);

  const loadPaymentMethods = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getPaymentMethods();
      data.sort((a, b) => (a.isDefault === b.isDefault ? 0 : a.isDefault ? -1 : 1));
      setPaymentMethods(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load payment methods');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetDefault = async (paymentMethodId: string) => {
    try {
      await setDefaultPaymentMethod(paymentMethodId);
      await loadPaymentMethods();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set default');
    }
  };

  const handleDelete = async (paymentMethodId: string) => {
    if (!confirm('Are you sure you want to remove this card?')) return;
    try {
      await deletePaymentMethod(paymentMethodId);
      await loadPaymentMethods();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove card');
    }
  };

  const handleAddNew = () => {
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleCardSaved = () => {
    setIsModalOpen(false);
    loadPaymentMethods();
  };

  const renderPaymentMethodCard = (pm: PaymentMethod) => {
    const brandInfo = getCardBrandInfo(pm.cardBrand);
    const isApplePay = pm.walletType === 'apple_pay';
    const isGooglePay = pm.walletType === 'google_pay';
    const walletLabel = isApplePay ? 'Apple Pay' : isGooglePay ? 'Google Pay' : null;

    return (
      <div key={pm.id} style={styles.card}>
        <div style={styles.cardHeader}>
          <div style={{ ...styles.cardIcon, backgroundColor: `${brandInfo.color}15` }}>
            <CreditCard size={20} color={brandInfo.color} />
          </div>
          <div style={styles.cardInfo}>
            <div style={styles.cardName}>
              {walletLabel
                ? `${walletLabel} (${brandInfo.name} ${pm.cardLast4})`
                : `${brandInfo.name} ending in ${pm.cardLast4}`}
              {pm.isDefault && (
                <span style={styles.defaultBadge}>Default</span>
              )}
            </div>
            <div style={styles.cardExpiry}>
              Expires {pm.cardExpMonth.toString().padStart(2, '0')}/{pm.cardExpYear.toString().slice(-2)}
            </div>
          </div>
        </div>
        <div style={styles.cardActions}>
          {!pm.isDefault && (
            <button
              style={styles.actionButton}
              onClick={() => handleSetDefault(pm.id)}
              title="Set as default"
            >
              <Check size={18} />
              Set Default
            </button>
          )}
          <button
            style={{ ...styles.actionButton, ...styles.deleteButton }}
            onClick={() => handleDelete(pm.id)}
            title="Remove"
          >
            <Trash2 size={18} />
            Remove
          </button>
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <div style={styles.container}>
        <div style={styles.content}>
          <button style={styles.backButton} onClick={() => navigate('/account')}>
            <ArrowLeft size={18} /> Back to Account
          </button>
          <h1 style={styles.title}>Payment Methods</h1>

          {error && <div style={styles.error}>{error}</div>}

          {isLoading ? (
            <div style={styles.loading}>Loading payment methods...</div>
          ) : (
            <>
              <button style={styles.addButton} onClick={handleAddNew}>
                <Plus size={20} />
                Add New Card
              </button>

              <div style={styles.applePayWrapper}>
                <AddApplePayButton
                  onSaved={loadPaymentMethods}
                  onError={(msg) => setError(msg)}
                />
              </div>

              {paymentMethods.length === 0 ? (
                <div style={styles.emptyState}>
                  <CreditCard size={48} color={colors.gray[400]} />
                  <p style={styles.emptyText}>No payment methods yet</p>
                  <p style={styles.emptySubtext}>
                    Add a card to place bids and make purchases
                  </p>
                </div>
              ) : (
                <div style={styles.cardList}>
                  {paymentMethods.map(renderPaymentMethodCard)}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {isModalOpen && (
        <AddCardModal onClose={handleModalClose} onSaved={handleCardSaved} />
      )}
    </Layout>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '24px',
  },
  content: {
    maxWidth: '600px',
    margin: '0 auto',
  },
  backButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: colors.gray[600],
    fontSize: 14,
    fontWeight: 500,
    padding: 0,
    marginBottom: 16,
  },
  title: {
    fontSize: 28,
    fontWeight: 700,
    color: colors.ink.black,
    marginBottom: 24,
  },
  error: {
    padding: '12px 16px',
    marginBottom: '16px',
    backgroundColor: '#FEE2E2',
    color: '#DC2626',
    borderRadius: '8px',
    fontSize: '14px',
  },
  loading: {
    textAlign: 'center',
    padding: '40px',
    color: colors.gray[600],
  },
  addButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    padding: '14px',
    marginBottom: '12px',
    fontSize: '15px',
    fontWeight: 600,
    color: colors.white,
    backgroundColor: colors.brand.blue,
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
  },
  applePayWrapper: {
    marginBottom: '24px',
  },
  cardList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  card: {
    padding: '16px',
    backgroundColor: colors.white,
    borderRadius: '12px',
    border: `1px solid ${colors.gray[300]}`,
  },
  cardHeader: {
    display: 'flex',
    gap: '12px',
    marginBottom: '12px',
  },
  cardIcon: {
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '10px',
    flexShrink: 0,
  },
  cardInfo: {
    flex: 1,
  },
  cardName: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '16px',
    fontWeight: 600,
    color: colors.ink.black,
    marginBottom: '4px',
  },
  defaultBadge: {
    fontSize: '11px',
    fontWeight: 600,
    color: colors.accent.purple,
    backgroundColor: '#EDE9FE',
    padding: '2px 8px',
    borderRadius: '100px',
  },
  cardExpiry: {
    fontSize: '14px',
    color: colors.gray[600],
  },
  cardActions: {
    display: 'flex',
    gap: '8px',
    paddingTop: '12px',
    borderTop: `1px solid ${colors.gray[100]}`,
  },
  actionButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '8px 12px',
    fontSize: '13px',
    fontWeight: 500,
    color: colors.gray[600],
    backgroundColor: colors.gray[100],
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  deleteButton: {
    color: '#DC2626',
    backgroundColor: '#FEE2E2',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '48px 24px',
    backgroundColor: colors.white,
    borderRadius: '12px',
    border: `1px solid ${colors.gray[300]}`,
  },
  emptyText: {
    fontSize: '18px',
    fontWeight: 600,
    color: colors.ink.black,
    marginTop: '16px',
    marginBottom: '4px',
  },
  emptySubtext: {
    fontSize: '14px',
    color: colors.gray[600],
    textAlign: 'center',
  },
};
